import React, {useState, useMemo} from "react";
import { format } from "date-fns";
import { Edit, OpenInNew, RemoveCircleOutline, Visibility } from "@material-ui/icons";
import { toast } from "react-toastify";

import useProjects from "./hooks/useProjects";
import { producaov2 } from "../../../services/apis";
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Link, MenuItem, Select, Switch, Typography } from "@material-ui/core";
import DataTable from "../../../components/DataTable";
import useAPI from "../../../hooks/useAPI";
import FeedbackSendOptionsModal from "./components/FeedbackSendOptionsModal";
import UserInfoModal from "../../../components/UserInfoModal";

const validFeedbackFileExtensions = [
  '.pdf',
  '.xls',
  '.xlsx',
];

const gradeOptions = [
  {
    key: '-',
    value: '-',
    text: '-'
  },
  {
    key: 'a',
    value: 'a',
    text: 'A'
  },
  {
    key: 'b',
    value: 'b',
    text: 'B'
  },
  {
    key: 'c',
    value: 'c',
    text: 'C'
  },
  {
    key: 'd',
    value: 'd',
    text: 'D'
  },
  {
    key: 'e',
    value: 'e',
    text: 'E'
  },
  {
    key: 'f',
    value: 'f',
    text: 'F'
  },
];

const updateProject = async ({ id, type, updateData }) => {
  return producaov2.put(`/admin/projetosV2/${id}?type=${type}`, updateData);
}

function ApprovedStatus({ id, type, hasExpired, isApproved: defaultIsApproved, canApprove, hasPendingFeedback }) {
  const [isApproved, setIsApproved] = useState(defaultIsApproved || false);
  const [loadingApproval, setLoadingApproval] = useState(false);

  const handleChangeIsApproved = (newIsApproved) => {
    const prevApproval = isApproved;

    try {
      setLoadingApproval(true);

      setIsApproved(newIsApproved);
      updateProject({ id, type, updateData: {isApproved: newIsApproved}});

      toast.success('Aprovação enviada com sucesso');
    } catch(error) {
      console.log(error);
      setIsApproved(prevApproval);

      toast.error('Houve um erro ao enviar aprovação');
    } finally {
      setLoadingApproval(false);
    }
  }

  return (isApproved || (canApprove && !hasExpired && !hasPendingFeedback)) ? (
    <Switch
      color="primary"
      size="small"
      disabled={loadingApproval}
      checked={isApproved}
      onChange={e => handleChangeIsApproved(e.target.checked)}
    />
  ) : (
    <Typography variant="body2">{
      'EM ANÁLISE'
    }</Typography>
  )
}

function Grade({id, type, shouldAssignGrade, grade: defaultGrade }) {
  const [grade, setGrade] = useState(defaultGrade?.toLowerCase());
  const [loadingChangeGrade, setLoadingChangeGrade] = useState(false);

  const handleChangeGrade = (newGrade) => {
    const prevGrade = grade;

    try {
      setLoadingChangeGrade(true);

      setGrade(newGrade);
      updateProject({id, type, updateData: {grade: newGrade?.toUpperCase()}});
    } catch(error) {
      toast.error('Houve um erro ao atualizar a nota');
      setGrade(prevGrade);
    } finally {
      setLoadingChangeGrade(false);
    }
  }

  return (
    shouldAssignGrade ? (
      <Select
        fullWidth
        disabled={!shouldAssignGrade || loadingChangeGrade}
        value={grade}
        onChange={e => handleChangeGrade(e.target.value)}
      >
        {gradeOptions.map(option => {
          return <MenuItem value={option.value}>{option.text}</MenuItem>
        })}
      </Select>
    ) : (
      <Typography variant="body2">Somente MBA</Typography>
    )
  )
}

function Feedback({
  steps,
  feedback: defaultFeedback,
  feedbackType: defaultFeedbackType,
  hideFeedbackButton,
  id,
  type,
  onSubmitFeedback,
  onResetFeedback
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [feedback, setFeedback] = useState(defaultFeedback);
  const [feedbackType, setFeedbackType] = useState(defaultFeedbackType);
  const [loadingResetFeedback, setLoadingResetFeedback] = useState(false);

  const toggleModalVisibility = () => {
    setModalVisible(prev => !prev);
  }

  const handleFeedbackSubmit = ({ feedback, type }) => {
    setFeedback(feedback);
    setFeedbackType(type);
    setModalVisible(false);

    onSubmitFeedback();
  }

  const handleResetFeedback = async () => {
    setLoadingResetFeedback(true);

    try {
      const response = await producaov2.delete(`/admin/projetosV2/${id}/feedback?type=${type}`);

      setFeedback("");
      setFeedbackType("file");

      onResetFeedback();

      toast.success("Feedback resetado com sucesso");
    } catch(error) {
      toast.error("Houve um erro ao resetar um feedback")
    } finally {
      setLoadingResetFeedback(false);
    }
  }

  return steps?.length === 0 && (
    <Box>
      {!hideFeedbackButton && (
        <IconButton title="Editar feedback" size="small" onClick={toggleModalVisibility}>
          <Edit />
        </IconButton>
      )}
      {feedback && (
        <IconButton title="Resetar feedback" size="small" disabled={loadingResetFeedback} onClick={handleResetFeedback}>
          {loadingResetFeedback ? <CircularProgress size={14} /> : <RemoveCircleOutline color="error" />}
        </IconButton>
      )}
      <FeedbackSendOptionsModal
        key={`${modalVisible}-${feedback}-${feedbackType}`}
        open={modalVisible}
        projectID={id}
        projectType={type}
        onSubmit={handleFeedbackSubmit}
        onClose={toggleModalVisibility}
        defaultFeedback={feedback}
        feedbackType={feedbackType}
      />
    </Box>
  );
}

function Certificate({ type, user, id }) {
  const certificateUrl = type === 'ppa' 
    ? `/admin/certificados/novo?type=specialist-lean-six-sigma&userId=${user?.id}`
    : `/admin/certificados/novo?type=curso&courseId=${id}&userId=${user?.id}`;

  return (
    <IconButton size="small" title="Emitir certificado" href={certificateUrl} target="_blank">
      <OpenInNew />
    </IconButton>
  )
}

function UserActions({ stepID, stepType, fileUrl: defaultFileUrl, onRefreshData }) {
  const [loadingDeleteStep, setLoadingDeleteStep] = useState(false);
  const [fileUrl, setFileUrl] = useState(defaultFileUrl);

  const handleResetSent = async () => {
    setLoadingDeleteStep(true);

    try {
      await producaov2.delete(`/admin/projetosV2/${stepID}?type=${stepType}`);

      setFileUrl('');
      toast.success('Envio do aluno resetado com sucesso');

      typeof onRefreshData === 'function' && onRefreshData();
    } catch(error) {
      console.log(error);
    } finally {
      setLoadingDeleteStep(false);
    }
  }

  return (
    <Box display="flex">
      <IconButton title="Visualizar envio do aluno" component="a" size="small" href={fileUrl} target="_blank">
        <Visibility />
      </IconButton>
      {!!fileUrl && (
        <IconButton title="Resetar envio do aluno" size="small" disabled={loadingDeleteStep} onClick={handleResetSent}>
          {loadingDeleteStep ? <CircularProgress size={14} /> : <RemoveCircleOutline color="error" />}
        </IconButton>
      )}
    </Box>
  )
}

const ControleProjetoNovo = () => {
  const {
    projects,
    page,
    isLoading,
    filters,
    rowsPerPage,
    onChangePage,
    onChangeFilters,
    refreshProjects,
    onChangeRowsPerPage
  } = useProjects();

  const {
    data: summaryProjects,
    isLoading: isLoadingSummary,
    refreshData: refreshSummary
  } = useAPI('/admin/projetosV2/count');

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUserID, setSelectedUserID] = useState(null);

  const handleDataUpdate = () => {
    refreshSummary();
    refreshProjects();
  };

  const resetSelectedProject = () => {
    setSelectedProject(null)
  }

  const resetSelectedUserID = () => {
    setSelectedUserID(null)
  }

  const handleSubmitFilters = (filters) => {
    onChangeFilters(filters);
  }

  const summaryRows = useMemo(() => {
    return summaryProjects?.map(project => {
      return {
        title: project?.title,
        in_analysis: project?.under_analysis || '0',
        not_approved: project?.not_approved || '0',
        approved: project?.approved || '0',
      }
    })
  }, [summaryProjects]);

  const projectsRows = useMemo(() => {
    return projects?.data?.map(project => {
      return {
        step: project?.steps?.length > 0 ? <Link component="button" underline="always" onClick={() => setSelectedProject(project)}>ETAPAS</Link> : null,
        project: project?.project?.name,
        last_sent_at: format(new Date(project?.lastSentAt), 'dd/MM/yyyy hh:mm:ss'),
        user: <Link component="button" underline="always" onClick={() => setSelectedUserID(project?.user?.id)}>{project?.user?.name}</Link>,
        send_actions: project?.steps?.length === 0 ? <UserActions {...project} stepID={project?.id} stepType={project?.type} /> : null,
        approved: (
          <ApprovedStatus
            {...project}
          />
        ),
        grade: (
          <Grade
            {...project}
          />
        ),
        feedback: (
          <Feedback
            {...project}
            onResetFeedback={handleDataUpdate}
            onSubmitFeedback={handleDataUpdate}
          />
        ),
        certificate: (
          <Certificate
            {...project}
          />
        ),
      }
    })
  }, [projects]);

  const projectFilterOptions = useMemo(() => {
    if(!summaryProjects) {
      return [];
    }

    return summaryProjects?.map(project => ({
      key: project?.id,
      name: project?.title
    }))
  }, [summaryProjects]);

  return (
    <Box display="flex" flexDirection="column" gridRowGap="1rem">
      <DataTable
        isSingleLine
        hasStickHeader
        maxHeight="180px"
        showPagination={false}
        rowsPerPage={6}
        isLoading={isLoadingSummary}
        columns={[
          { key: 'title', name: 'Aplicações' },
          { key: 'in_analysis', name: 'Em análise' },
          { key: 'approved', name: 'Aprovados' },
          { key: 'not_approved', name: 'Não aprovados' },
        ]}
        rows={summaryRows}
      />
      <DataTable
        isLoading={isLoading}
        showFilters
        filters={[
          { key: 'user', name: 'Aluno', component: { type: 'text' } },
          { key: 'courseID', name: 'Filtrar por projeto', component: { type: 'select', options: [{ key: 'all', name: 'Todos projetos' }, ...projectFilterOptions] } },
          { key: 'status', name: 'Filtrar por status', component: { type: 'select', options: [{ key: 'under_analysis', name: 'Em análise' }, { key: 'not_approved', name: 'Não aprovados' }, { key: 'approved', name: 'Aprovados' }] } },
          { key: 'ordering', name: 'Ordenação', component: { type: 'select', options: [{ key: 'desc', name: 'Mais novo' }, { key: 'asc', name: 'Mais antigo' }] } },
        ]}
        columns={[
          { key: 'step', name: '' },
          { key: 'project', name: 'Projeto', isSingleLine: true, maxWidth: '300px' },
          { key: 'last_sent_at', name: 'Último envio', isSingleLine: true },
          { key: 'user', name: 'Aluno', isSingleLine: true, maxWidth: '300px' },
          { key: 'send_actions', name: 'Envio' },
          { key: 'approved', name: 'Aprovado?', isSingleLine: true, maxWidth: '240px'},
          { key: 'grade', name: 'Nota', isSingleLine: true },
          { key: 'feedback', name: 'Feedback' },
          { key: 'certificate', name: 'Certificado' }
        ]}
        rowsPerPage={rowsPerPage}
        rows={projectsRows}
        page={page}
        selectedFilters={filters}
        totalRecords={projects?.total}
        onPageChange={onChangePage}
        onRowsPerChange={onChangeRowsPerPage}
        onSubmitFilters={handleSubmitFilters}
        onResetFilters={() => handleSubmitFilters({})}
      />
      <Dialog
        open={!!selectedProject}
        onClose={resetSelectedProject}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {`Etapas ${selectedProject?.project?.name}`}
          <br />
          {`[${selectedProject?.user?.name}]`}
        </DialogTitle>
        <DialogContent>
          <DataTable
            showPagination={false}
            columns={[
              { key: 'step', name: 'Etapa' },
              { key: 'created_at', name: 'Data' },
              { key: 'send_actions', name: 'Envio' },
              { key: 'feedback', name: 'Feedback' }  
            ]}
            rows={selectedProject?.steps?.map(step => {
              return {
                id: step?.id,
                step: step?.step,
                created_at: format(new Date(step?.sentAt), 'dd/MM/yyyy hh:mm:ss'),
                send_actions: (
                  <UserActions fileUrl={step?.fileUrl} stepID={step?.id} stepType={step?.type} onRefreshData={handleDataUpdate}/>
                ),
                feedback: (
                  <Feedback
                    feedback={step?.feedback}
                    feedbackType={step?.feedbackType}
                    onSubmitFeedback={handleDataUpdate}
                    onResetFeedback={handleDataUpdate}
                    id={step?.id}
                    type={step?.type}
                    hideFeedbackButton={step?.hideFeedbackButton}
                    steps={[]}
                  />
                )
              }
            })}
          />
        </DialogContent>
      </Dialog>
      {selectedUserID && (
        <UserInfoModal userID={selectedUserID} onClose={resetSelectedUserID} />
      )}
    </Box>
  )
};

export default ControleProjetoNovo;
